import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Button, Card, Table, Td, Th } from '@/components/admin/ui';
import {
  BarList,
  ChartCard,
  DeviceLink,
  EmptyState,
  KpiTile,
  RangePicker,
  UserLink,
} from '@/components';
import { VillageArtLabel, VillageArtMark } from '@/components/VillageArtLabel';
import { track } from '@/lib/analytics';
import { PRIMARY_HEX } from '@/lib/colors';
import { formatAdminDateTime, formatDurationSeconds, formatInt } from '@/lib/format';
import { apiJson } from '@/lib/api';
import { VILLAGE_HEADER_CAST, villageSpriteForKey } from '@/lib/village-analytics-art';

type VillageRange = '7d' | '30d' | '90d' | 'all';

type CountRow = { key: string; count: number };

type VillageAnalytics = {
  range: VillageRange;
  headlines: {
    players: number;
    devices: number;
    sessions: number;
    play_seconds: number;
    subscribers: number;
    coins_in_circulation: number;
    cloud_saves: number;
    median_level: number;
    words: number;
    quests_completed: number;
  };
  activity: { players: number; sessions: number; play_seconds: number };
  days: { date: string; sessions: number; play_seconds: number }[];
  coins: {
    key: string;
    label: string;
    account_id: string | null;
    installation_id: string;
    coins: number;
    peak_coins: number;
    level: number;
  }[];
  levels: CountRow[];
  xp_by_source: CountRow[];
  words: CountRow[];
  quests: CountRow[];
  areas: CountRow[];
  avatars: CountRow[];
  platforms: CountRow[];
  rods: CountRow[];
  items: CountRow[];
  fish: { key: string; players: number; best_weight: number }[];
  recent_sessions: {
    run_id: number | null;
    played_at: string;
    label: string;
    account_id: string | null;
    installation_id: string;
    platform: string | null;
    score: number;
    duration_seconds: number | null;
    coins: number;
    level: number;
    words: number;
    quests: number;
    harvests: number;
    forages: number;
    catches: number;
    mines: number;
  }[];
};

type AmplitudePayload = {
  range: VillageRange;
  totals: Record<string, number>;
  farming: { key: string; label: string; count: number }[];
  fishing: { key: string; label: string; count: number }[];
  groups: Record<string, CountRow[]>;
  error: string | null;
};

const RANGES: VillageRange[] = ['7d', '30d', '90d', 'all'];

const HIDE_TEST_ACCOUNTS_KEY = 'admin.village.hideTestAccounts';

function readHideTestAccounts(): boolean {
  try {
    const value = window.localStorage.getItem(HIDE_TEST_ACCOUNTS_KEY);
    if (value === null) return true;
    return value === '1' || value === 'true';
  } catch {
    return true;
  }
}

function writeHideTestAccounts(hidden: boolean) {
  try {
    window.localStorage.setItem(HIDE_TEST_ACCOUNTS_KEY, hidden ? '1' : '0');
  } catch {
    // Private mode or a full quota should not block the current view.
  }
}

const HINTS = {
  players: 'Distinct village players, all time. Signed-in devices that share an account count as one player.',
  devices: 'Distinct installations that submitted at least one Hmong Village session, all time.',
  sessions: 'Hmong Village game runs submitted, all time.',
  play: 'Sum of session durations, all time.',
  subscribers: 'Players whose account is subscribed or has a subscription override, all time.',
  coins: 'Sum of current coin balances. Cloud saves win over the last session.',
  chartCoins: 'Top players by current coin balance. Peak is the highest balance seen on any session. The range limits who counts as active.',
  daily: 'Sessions per Pacific calendar day in the selected range.',
} as const;

function fmtDay(iso: string) {
  const [, m, day] = iso.split('-');
  return `${m}/${day}`;
}

function chartTooltipFormatter(value: unknown, name: unknown) {
  const v = typeof value === 'number' ? formatInt(value) : String(value);
  return [v, String(name)] as [string, string];
}

function PlayerName({
  label,
  accountId,
  installationId,
  className,
}: {
  label: string;
  accountId: string | null;
  installationId: string;
  className?: string;
}) {
  if (accountId) {
    return <UserLink userId={accountId} displayName={label} className={className} />;
  }
  return (
    <DeviceLink
      installationId={installationId}
      nickname={label}
      label="nickname"
      className={className}
    />
  );
}

type CoinPlayer = VillageAnalytics['coins'][number];

const COIN_TICK_LINK =
  'block w-full cursor-pointer whitespace-normal break-words text-right text-[11px] leading-tight';

export function CoinPlayerTick({
  x = 0,
  y = 0,
  index,
  payload,
  players,
}: {
  x?: number;
  y?: number;
  index?: number;
  payload?: { value?: unknown; index?: number };
  players: CoinPlayer[];
}) {
  const tickIndex = payload?.index ?? index;
  const player =
    (typeof tickIndex === 'number' ? players[tickIndex] : undefined) ??
    players.find((row) => row.label === payload?.value);
  const label = player?.label ?? (payload?.value == null ? '' : String(payload.value));

  return (
    <g transform={`translate(${x},${y})`}>
      <foreignObject x={-116} y={-16} width={112} height={32}>
        <div className="flex h-full w-full items-center justify-end">
          {player ? (
            <span
              className="block w-full"
              onClick={() =>
                track('Hmong Village Open Player', {
                  source: 'Current coins',
                  link: player.account_id ? 'account' : 'device',
                })
              }
            >
              <PlayerName
                label={label}
                accountId={player.account_id}
                installationId={player.installation_id}
                className={COIN_TICK_LINK}
              />
            </span>
          ) : (
            <span className="block w-full text-right text-[11px] leading-tight text-nova-muted-foreground">
              {label}
            </span>
          )}
        </div>
      </foreignObject>
    </g>
  );
}

function artRows(rows: CountRow[]) {
  return rows.map((row) => {
    const sprite = villageSpriteForKey(row.key);
    if (!sprite) return row;
    return {
      ...row,
      label: (
        <VillageArtLabel sprite={sprite} size={22}>
          {row.key}
        </VillageArtLabel>
      ),
    };
  });
}

function BreakdownCard({
  title,
  hint,
  rows,
  sprite,
}: {
  title: string;
  hint: string;
  rows: CountRow[];
  sprite?: string;
}) {
  return (
    <Card padding="md" title={hint}>
      <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold text-nova-foreground">
        {sprite ? <VillageArtMark sprite={sprite} size={28} /> : null}
        {title}
      </h2>
      <BarList data={artRows(rows)} emptyLabel="No activity in this range" />
    </Card>
  );
}

export function VillagePage() {
  const { range } = Route.useSearch();
  const navigate = useNavigate({ from: '/admin/village' });
  const qc = useQueryClient();
  const [hideTestAccounts, setHideTestAccounts] = useState(readHideTestAccounts);

  useEffect(() => {
    track('Hmong Village Analytics');
  }, []);

  const statsQ = useQuery({
    queryKey: ['admin', 'village', range, hideTestAccounts],
    queryFn: () => {
      const params = new URLSearchParams({ range });
      if (hideTestAccounts) params.set('exclude_test', '1');
      return apiJson<VillageAnalytics>(`/v1/admin/analytics/village?${params}`);
    },
  });

  const ampQ = useQuery({
    queryKey: ['admin', 'village', 'amplitude', range],
    queryFn: () =>
      apiJson<AmplitudePayload>(
        `/v1/admin/analytics/village/amplitude?range=${encodeURIComponent(range)}`
      ),
  });

  const stats = statsQ.data;
  const headlines = stats?.headlines;
  const days = useMemo(
    () => stats?.days.map((day) => ({ ...day, label: fmtDay(day.date) })) ?? [],
    [stats]
  );
  const coinRows = stats?.coins ?? [];
  const amp = ampQ.data;
  const groups = amp?.groups ?? {};

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex flex-wrap items-end gap-3">
            <h1 className="text-xl font-semibold tracking-tight text-nova-foreground">Hmong Village</h1>
            <div className="flex items-end" aria-hidden>
              {VILLAGE_HEADER_CAST.map((sprite) => (
                <VillageArtMark key={sprite} sprite={sprite} size={40} />
              ))}
            </div>
          </div>
          <p className="mt-1 text-sm text-nova-muted-foreground">
            Headline numbers are all-time. Charts, activity, and Amplitude follow the range.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <label
            className="inline-flex cursor-pointer select-none items-center gap-2 rounded-lg border border-nova-border bg-nova-card h-8 px-2.5 text-sm font-medium text-nova-foreground shadow-sm transition hover:border-nova-input"
            title="Leaves littlest_learner and hmong_queen out of player stats, charts, and sessions. Amplitude events are not tied to accounts, so those totals stay as-is."
          >
            <input
              type="checkbox"
              checked={hideTestAccounts}
              onChange={(event) => {
                const hidden = event.target.checked;
                writeHideTestAccounts(hidden);
                setHideTestAccounts(hidden);
                track('Hmong Village Hide Test Accounts', { hidden });
              }}
              className="h-4 w-4 rounded border-nova-input text-nova-primary focus:ring-nova-ring"
            />
            <span>
              Hide test accounts
              <span className="ml-1 font-normal text-nova-muted-foreground">littlest_learner, hmong_queen</span>
            </span>
          </label>
          <RangePicker
            value={range}
            options={RANGES}
            onChange={(next) => {
              void navigate({ search: { range: next } });
            }}
          />
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              void qc.invalidateQueries({ queryKey: ['admin', 'village'] });
            }}
          >
            Refresh
          </Button>
        </div>
      </div>

      {statsQ.error && (
        <p className="text-sm text-red-600">
          {(statsQ.error as Error).message}
          <button
            type="button"
            className="ml-2 underline"
            onClick={() => void statsQ.refetch()}
          >
            Retry
          </button>
        </p>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <KpiTile
          label="Players"
          value={headlines?.players ?? 0}
          loading={statsQ.isLoading}
          tooltip={HINTS.players}
          icon={<VillageArtMark sprite="player_down0" />}
        />
        <KpiTile label="Devices" value={headlines?.devices ?? 0} loading={statsQ.isLoading} tooltip={HINTS.devices} />
        <KpiTile
          label="Sessions"
          value={headlines?.sessions ?? 0}
          loading={statsQ.isLoading}
          tooltip={HINTS.sessions}
          icon={<VillageArtMark sprite="player_down1" />}
        />
        <KpiTile
          label="Play time"
          value={formatDurationSeconds(headlines?.play_seconds ?? 0)}
          loading={statsQ.isLoading}
          tooltip={HINTS.play}
        />
        <KpiTile
          label="Subscribers"
          value={headlines?.subscribers ?? 0}
          loading={statsQ.isLoading}
          tooltip={HINTS.subscribers}
        />
        <KpiTile
          label="Coins"
          value={headlines?.coins_in_circulation ?? 0}
          loading={statsQ.isLoading}
          tooltip={HINTS.coins}
          icon={<VillageArtMark sprite="stall" />}
        />
        <KpiTile label="Cloud saves" value={headlines?.cloud_saves ?? 0} loading={statsQ.isLoading} />
        <KpiTile label="Median level" value={headlines?.median_level ?? 0} loading={statsQ.isLoading} />
        <KpiTile
          label="Words"
          value={headlines?.words ?? 0}
          loading={statsQ.isLoading}
          icon={<VillageArtMark sprite="tipSign" />}
        />
        <KpiTile
          label="Quests done"
          value={headlines?.quests_completed ?? 0}
          loading={statsQ.isLoading}
          icon={<VillageArtMark sprite="npcPog" />}
        />
      </div>

      <p className="text-sm text-nova-muted-foreground">
        In this range: {formatInt(stats?.activity.players ?? 0)} active players ·{' '}
        {formatInt(stats?.activity.sessions ?? 0)} sessions ·{' '}
        {formatDurationSeconds(stats?.activity.play_seconds ?? 0)} played
      </p>

      <div className="grid gap-4 lg:grid-cols-2">
        <ChartCard
          title="Daily sessions"
          tooltip={HINTS.daily}
          height={280}
          loading={statsQ.isLoading}
          right={<VillageArtMark sprite="player_down1" />}
        >
          {days.length === 0 ? (
            <EmptyState title="No sessions in this range" />
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={days}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip formatter={chartTooltipFormatter} />
                <Bar dataKey="sessions" name="Sessions" fill={PRIMARY_HEX} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard
          title="Current coins"
          tooltip={HINTS.chartCoins}
          height={Math.max(280, coinRows.length * 36)}
          loading={statsQ.isLoading}
          right={<VillageArtMark sprite="stall" />}
        >
          {coinRows.length === 0 ? (
            <EmptyState title="No active players in this range" />
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={coinRows} layout="vertical" margin={{ left: 8, right: 16 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                <YAxis
                  type="category"
                  dataKey="label"
                  width={124}
                  interval={0}
                  tick={<CoinPlayerTick players={coinRows} />}
                />
                <Tooltip formatter={chartTooltipFormatter} />
                <Bar dataKey="coins" name="Current coins" fill={PRIMARY_HEX} radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <BreakdownCard title="Level" hint="Current level of players active in the range." rows={stats?.levels ?? []} sprite="player_down0" />
        <BreakdownCard title="XP by source" hint="XP stored on cloud saves of active players." rows={stats?.xp_by_source ?? []} sprite="seedling" />
        <BreakdownCard title="Current area" hint="areaId on the cloud save." rows={stats?.areas ?? []} sprite="well" />
        <BreakdownCard title="Avatar" hint="Chosen avatar on the cloud save." rows={stats?.avatars ?? []} sprite="player_f_down0" />
        <BreakdownCard title="Platform" hint="Platforms of devices that played in the range." rows={stats?.platforms ?? []} />
        <BreakdownCard title="Fishing rod" hint="rodLevel on the cloud save. hasRod without a level counts as rod 1." rows={stats?.rods ?? []} sprite="toolFishingRod" />
        <BreakdownCard title="Words" hint="Players whose cloud save lists each discovered word." rows={stats?.words ?? []} sprite="tipSign" />
        <BreakdownCard title="Quests" hint="Players whose cloud save lists each completed quest." rows={stats?.quests ?? []} sprite="npcPog" />
        <BreakdownCard title="Bag contents" hint="Item counts summed across active players' cloud saves." rows={stats?.items ?? []} sprite="toolBasket" />
      </div>

      <Card padding="md">
        <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold text-nova-foreground">
          <VillageArtMark sprite="fish_carp" size={28} />
          Trophy fish
        </h2>
        {stats?.fish.length ? (
          <Table>
            <thead>
              <tr>
                <Th>Fish</Th>
                <Th>Players</Th>
                <Th>Best weight</Th>
              </tr>
            </thead>
            <tbody>
              {stats.fish.map((fish) => (
                <tr key={fish.key}>
                  <Td>
                    <VillageArtLabel sprite={villageSpriteForKey(fish.key)} size={22}>
                      {fish.key}
                    </VillageArtLabel>
                  </Td>
                  <Td>{formatInt(fish.players)}</Td>
                  <Td>{fish.best_weight}</Td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          <EmptyState title="No trophy fish on active saves" />
        )}
      </Card>

      <div>
        <h2 className="mb-1 text-sm font-semibold text-nova-foreground">Amplitude</h2>
        <p className="mb-3 text-sm text-nova-muted-foreground">
          Hmonglus event totals in the selected range. Fish, crops, forage, and shop items are grouped by their event property.
          {hideTestAccounts
            ? ' These totals still include the test accounts, because Amplitude events are not tied to display names.'
            : ''}
        </p>
        {amp?.error && <p className="mb-3 text-sm text-red-600">{amp.error}</p>}
        <div className="grid items-start gap-4 lg:grid-cols-2">
          <ChartCard
            title="Game opens"
            height={220}
            loading={ampQ.isLoading}
            right={<VillageArtMark sprite="player_down0" />}
          >
            <p className="text-3xl font-semibold tabular-nums text-nova-foreground">
              {formatInt(amp?.totals.game_screen ?? 0)}
            </p>
            <p className="mt-1 text-sm text-nova-muted-foreground">Hmong Village Game Screen</p>
          </ChartCard>
          <ChartCard
            title="Farming"
            height={220}
            loading={ampQ.isLoading}
            right={<VillageArtMark sprite="toolHoe" />}
          >
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={amp?.farming ?? []}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip formatter={chartTooltipFormatter} />
                <Bar dataKey="count" name="Events" fill={PRIMARY_HEX} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
          <ChartCard
            title="Fishing funnel"
            height={220}
            loading={ampQ.isLoading}
            right={<VillageArtMark sprite="toolFishingRod" />}
          >
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={amp?.fishing ?? []}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip formatter={chartTooltipFormatter} />
                <Bar dataKey="count" name="Events" fill={PRIMARY_HEX} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
          <BreakdownCard title="Shop buys" hint="Item Bought, grouped by item." rows={groups.items_bought ?? []} sprite="seedSack" />
          <BreakdownCard title="Shop sells" hint="Item Sold, grouped by item." rows={groups.items_sold ?? []} sprite="stall" />
          <BreakdownCard title="Fish caught" hint="Fish Caught, grouped by fish." rows={groups.fish ?? []} sprite="fish_carp" />
          <BreakdownCard title="Crops planted" hint="Crop Planted, grouped by crop." rows={groups.crops_planted ?? []} sprite="seedling" />
          <BreakdownCard title="Crops harvested" hint="Crop Harvested, grouped by crop." rows={groups.crops_harvested ?? []} sprite="riceReady" />
          <BreakdownCard title="Crops died" hint="Crop Died, grouped by crop." rows={groups.crops_died ?? []} sprite="seedling" />
          <BreakdownCard title="Forage" hint="Forage Picked, grouped by kind." rows={groups.forage ?? []} sprite="berryBushFull" />
          <BreakdownCard title="NPCs" hint="NPC Talked, grouped by npc." rows={groups.npcs ?? []} sprite="npcYawg" />
          <BreakdownCard title="Quests started" hint="Quest Started, grouped by quest." rows={groups.quests_started ?? []} sprite="npcPog" />
          <BreakdownCard title="Quests completed" hint="Quest Completed, grouped by quest." rows={groups.quests_completed ?? []} sprite="npcPog" />
          <BreakdownCard title="Words discovered" hint="Word Discovered, grouped by word." rows={groups.words ?? []} sprite="tipSign" />
          <BreakdownCard title="Areas entered" hint="Area Entered, grouped by areaId." rows={groups.areas ?? []} sprite="bridge" />
          <BreakdownCard title="Level-ups" hint="Level Up, grouped by level." rows={groups.levels ?? []} sprite="player_down1" />
        </div>
      </div>

      <div>
        <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold text-nova-foreground">
          <VillageArtMark sprite="player_down1" size={28} />
          Recent sessions
        </h2>
        {stats?.recent_sessions.length ? (
          <Card>
            <div className="max-w-full overflow-x-auto">
              <Table>
                <thead>
                  <tr>
                    <Th>When</Th>
                    <Th>Player</Th>
                    <Th>Platform</Th>
                    <Th>
                      <VillageArtLabel sprite="stall" size={18}>
                        Coins
                      </VillageArtLabel>
                    </Th>
                    <Th>Level</Th>
                    <Th>
                      <VillageArtLabel sprite="tipSign" size={18}>
                        Words
                      </VillageArtLabel>
                    </Th>
                    <Th>
                      <VillageArtLabel sprite="npcPog" size={18}>
                        Quests
                      </VillageArtLabel>
                    </Th>
                    <Th>
                      <VillageArtLabel sprite="riceReady" size={18}>
                        Harvests
                      </VillageArtLabel>
                    </Th>
                    <Th>
                      <VillageArtLabel sprite="berryBushFull" size={18}>
                        Forage
                      </VillageArtLabel>
                    </Th>
                    <Th>
                      <VillageArtLabel sprite="fish_carp" size={18}>
                        Catches
                      </VillageArtLabel>
                    </Th>
                    <Th>
                      <VillageArtLabel sprite="rockFull" size={18}>
                        Mines
                      </VillageArtLabel>
                    </Th>
                    <Th>Time</Th>
                  </tr>
                </thead>
                <tbody>
                  {stats.recent_sessions.map((row) => (
                    <tr key={`${row.run_id ?? row.played_at}-${row.installation_id}`}>
                      <Td className="whitespace-nowrap text-xs text-nova-muted-foreground">
                        {formatAdminDateTime(row.played_at)}
                      </Td>
                      <Td>
                        <PlayerName
                          label={row.label}
                          accountId={row.account_id}
                          installationId={row.installation_id}
                        />
                      </Td>
                      <Td>{row.platform ?? '—'}</Td>
                      <Td>{formatInt(row.coins)}</Td>
                      <Td>{formatInt(row.level)}</Td>
                      <Td>{formatInt(row.words)}</Td>
                      <Td>{formatInt(row.quests)}</Td>
                      <Td>{formatInt(row.harvests)}</Td>
                      <Td>{formatInt(row.forages)}</Td>
                      <Td>{formatInt(row.catches)}</Td>
                      <Td>{formatInt(row.mines)}</Td>
                      <Td className="whitespace-nowrap text-xs text-nova-muted-foreground">
                        {formatDurationSeconds(row.duration_seconds)}
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </Card>
        ) : (
          <EmptyState title="No sessions in this range" />
        )}
      </div>
    </div>
  );
}

export const Route = createFileRoute('/admin/village')({
  validateSearch: (raw: Record<string, unknown>): { range: VillageRange } => {
    const value = raw.range;
    const range: VillageRange =
      value === '7d' || value === '30d' || value === '90d' || value === 'all' ? value : '30d';
    return { range };
  },
  component: VillagePage,
});
